// WMS mobile — local session helpers voor gedeeld apparaat.
//
// device_token: langlopende JWT (30d) — één per TC27
// active_user_id + active_user_name: bewaard in localStorage, geroteerd via
//   pincode-verify. Elke mutation stuurt X-Active-User-Id header mee.
//
// Voor MVP kunnen we device_token weglaten en gewoon standaard user-JWT
// gebruiken; active_user_id fungeert dan als "wie is nu bezig" hint. Bij
// implementatie van echte device-JWT flow wordt fetchWithScanAuth aangepast.

const LS_ACTIVE_USER_ID = "wms.active_user_id";
const LS_ACTIVE_USER_NAME = "wms.active_user_name";
const LS_ACTIVE_USER_ROLE = "wms.active_user_role";
const LS_DEVICE_LABEL = "wms.device_label";
const LS_ACTIVE_SESSION_TOKEN = "wms.active_session_token";
const LS_ACTIVE_SESSION_EXPIRES = "wms.active_session_expires";

export interface ActiveUser {
  id: number;
  name: string;
  role: string;
}

export function getActiveUser(): ActiveUser | null {
  try {
    const idStr = localStorage.getItem(LS_ACTIVE_USER_ID);
    if (!idStr) return null;
    const id = Number(idStr);
    if (!Number.isInteger(id) || id <= 0) return null;
    return {
      id,
      name: localStorage.getItem(LS_ACTIVE_USER_NAME) ?? "onbekend",
      role: localStorage.getItem(LS_ACTIVE_USER_ROLE) ?? "warehouse",
    };
  } catch {
    return null;
  }
}

export function setActiveUser(u: ActiveUser, sessionToken?: string, expiresAt?: string): void {
  try {
    localStorage.setItem(LS_ACTIVE_USER_ID, String(u.id));
    localStorage.setItem(LS_ACTIVE_USER_NAME, u.name);
    localStorage.setItem(LS_ACTIVE_USER_ROLE, u.role);
    if (sessionToken) localStorage.setItem(LS_ACTIVE_SESSION_TOKEN, sessionToken);
    if (expiresAt) localStorage.setItem(LS_ACTIVE_SESSION_EXPIRES, expiresAt);
  } catch { /* storage vol / disabled */ }
}

export function clearActiveUser(): void {
  try {
    localStorage.removeItem(LS_ACTIVE_USER_ID);
    localStorage.removeItem(LS_ACTIVE_USER_NAME);
    localStorage.removeItem(LS_ACTIVE_USER_ROLE);
    localStorage.removeItem(LS_ACTIVE_SESSION_TOKEN);
    localStorage.removeItem(LS_ACTIVE_SESSION_EXPIRES);
  } catch { /* ignore */ }
}

/** Actief session-token (X-Active-Session header) — voorkomt spoofing van
 *  user_id op mutation-endpoints. Verlopen? Return null; UI moet opnieuw pincode. */
export function getActiveSessionToken(): string | null {
  try {
    const token = localStorage.getItem(LS_ACTIVE_SESSION_TOKEN);
    if (!token) return null;
    const exp = localStorage.getItem(LS_ACTIVE_SESSION_EXPIRES);
    if (exp && new Date(exp).getTime() < Date.now()) {
      // Verlopen → wis + return null
      localStorage.removeItem(LS_ACTIVE_SESSION_TOKEN);
      localStorage.removeItem(LS_ACTIVE_SESSION_EXPIRES);
      return null;
    }
    return token;
  } catch { return null; }
}

export function getDeviceLabel(): string | null {
  try { return localStorage.getItem(LS_DEVICE_LABEL); } catch { return null; }
}

export function setDeviceLabel(label: string): void {
  try { localStorage.setItem(LS_DEVICE_LABEL, label); } catch { /* ignore */ }
}
